import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@components/Card';
import { useAuth } from '@hooks/useAuth';

/**
 * Login page component
 * Handles user authentication via Keycloak
 */
export function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    await login();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md glass rounded-xl overflow-hidden">
        <div className="text-center p-8 pb-6">
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full gradient-primary">
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome to RenovX
          </h1>
        </div>

        <div className="p-8 pt-0 space-y-6">
          <p className="text-center text-gray-600">
            The comprehensive modernization platform for transforming your legacy codebase with
            intelligent analysis and automated code generation.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-400"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="font-medium text-gray-900">Code Analysis</h3>
                <p className="text-sm text-gray-600">
                  Reverse engineer and analyze your existing codebase
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-secondary-400"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="font-medium text-gray-900">Modernization</h3>
                <p className="text-sm text-gray-600">
                  Generate modern code, tests, and documentation
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-400"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="font-medium text-gray-900">Best Practices</h3>
                <p className="text-sm text-gray-600">
                  Follow modern patterns and security standards
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleLogin}
            fullWidth
            size="lg"
            className="mt-8 bg-primary-600 hover:bg-primary-700 text-white border-0"
          >
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}
